import { ChevronDown } from "lucide-react";
import { Button } from "@satyrsmc/shared/components";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** Checkbox-dropdown filter for "select any of these values, or none" —
 * an empty selection reads as "no restriction," distinct from a single
 * selected option. Built for the events Attendees table's Status/Payment
 * filters; generic enough for any admin table filtering on a small,
 * multi-valued enum. */
export function MultiSelectFilter<T extends string>({
  label,
  pluralLabel,
  anyLabel,
  options,
  selected,
  onChange,
  widthClassName = "w-[140px]",
}: {
  label: string;
  pluralLabel: string;
  /** Text for the "no restriction" option, e.g. "Any payment status" instead
   * of the default "Any {label}" when that reads awkwardly on its own. */
  anyLabel?: string;
  options: { value: T; label: string }[];
  selected: Set<T>;
  onChange: (next: Set<T>) => void;
  widthClassName?: string;
}) {
  const any = anyLabel ?? `Any ${label}`;
  const summary =
    selected.size === 0
      ? any
      : selected.size === options.length
        ? `All ${pluralLabel}`
        : selected.size === 1
          ? (options.find((o) => selected.has(o.value))?.label ?? label)
          : `${selected.size} ${pluralLabel}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`${widthClassName} justify-between font-normal text-foreground`}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="size-4 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuCheckboxItem
          checked={selected.size === 0}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={(checked) => {
            // Mutually exclusive with the specific options below — picking
            // "Any" clears them; there's nothing to do when unchecking "Any"
            // itself (it stays checked until a specific option is picked).
            if (checked) onChange(new Set());
          }}
        >
          {any}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={selected.has(opt.value)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) => {
              const next = new Set(selected);
              if (checked) next.add(opt.value);
              else next.delete(opt.value);
              onChange(next);
            }}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
